import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { format } from 'date-fns';
import React, { FC, Fragment, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { mapAgeToColor } from '../../helpers';
import useTicketsRefinement from '../TicketsRefinement/hooks/useTicketsRefinement';
import TicketsSorting from '../TicketsSorting/TicketsSorting';
import TicketsListViewLoader from './components/TicketsListViewLoader';
import {
  ApprovedStyled,
  BreakWordsContentStyled,
  ContentStyled,
  HasAttachmentStyled,
  ListViewStyled,
  TableStyled,
  TrStyled,
} from './TicketsListView.styled';
import { uploadUserColumnPreferences } from 'api/user';
import DotsLoader from 'components/DotsLoader/DotsLoader';
import PriorityIcon from 'components/PriorityIcon/PriorityIcon';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { TicketAssigneeE } from 'declarations/enums/tickets';
import { LastMessageI } from 'declarations/interfaces/tickets';
import { Column, NoopT } from 'declarations/types/common';
import { formatAmount } from 'helpers/locale';
import { getInitials } from 'helpers/string';
import { useFetchUserColumnPreferences } from 'hooks/api/useUser';
import ApproveIcon from 'icons/ApproveIcon';
import { MessageWrapStyled } from 'pages/TicketsPage/TicketsPage.styled';

export type TicketsListViewPropsT = {
  ticketsCount: number;
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  isLoadingTickets: boolean;
  onSelectTicket: (id: string) => NoopT;
  isViewed?: boolean;
  columns: Column[];
};

type ColumnVisibilityMap = { [key: string]: boolean };

const TicketsListView: FC<TicketsListViewPropsT> = ({
  ticketsPages,
  ticketsCount,
  isLoadingTickets,
  onSelectTicket,
  columns,
}) => {
  /**
   * Hooks
   */
  const { isMobile } = useResponsive();
  const {
    isSuccessfullyFetchedTickets,
    isFetchingNextTicketsPage,
    isFetchingTickets,
    hasNextTicketsPage,
    fetchNextTicketsPage,
    selectedRoofTop,
    selectedTicketsIds,
    setSelectedTicketsIds,
  } = useTickets();
  const {
    isOpenStatusFilterActive,
    isCloseStatusFilterActive,
    isUnder24HoursFilterActive,
    isOver24HoursFilterActive,
    isAssignedToMeFilterActive,
    isPastMonthFilterActive,
    isPast30DaysFilterActive,
    isArchivedFilterActive,
  } = useTicketsRefinement();
  const { ref, inView } = useInView();
  const lastTicketObserver = ref;

  const userColumnPreferences = useFetchUserColumnPreferences({
    onSuccess: data => {
      localStorage.setItem('visibleColumns', data);
    },
    onError: err => {
      if (err && err.data?.detail?.startsWith('The specified key does not exist.')) {
        const defaultColumnVisibility: ColumnVisibilityMap = {};
        columns.forEach(column => {
          defaultColumnVisibility[column.id] = true;
        });

        uploadUserColumnPreferences({ RawData: JSON.stringify(columns) });
      }
    },
  });

  const storedVisibleColumnsJSON =
    userColumnPreferences.data ?? localStorage.getItem('visibleColumns');
  const storedVisibleColumns = storedVisibleColumnsJSON ? JSON.parse(storedVisibleColumnsJSON) : [];

  const orderedColumns = storedVisibleColumns
    ? storedVisibleColumns.map((storedColumnId: string) => {
        const matchingColumn = columns.find(column => column.id === storedColumnId);
        return matchingColumn ? matchingColumn : storedColumnId;
      })
    : columns;

  const visibleColumnCount =
    storedVisibleColumns.filter((column: Column) => column.visible).length + 1;

  const handleTicketSelection = (ticketId: string) => {
    if (selectedTicketsIds.includes(ticketId)) {
      setSelectedTicketsIds(selectedTicketsIds.filter(id => id !== ticketId));
    } else {
      setSelectedTicketsIds([...selectedTicketsIds, ticketId]);
    }
  };

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>, id: string) => {
    const target = event.target as HTMLElement;

    const tagName = target.tagName.toLowerCase();
    const inputType = (target as HTMLInputElement).type;

    const isCheckboxClicked = tagName === 'input' && inputType === 'checkbox';

    if (!isCheckboxClicked) {
      onSelectTicket(id).call(id);
    }
  };

  /**
   * Variables
   */
  const shouldLoadMore = isFetchingTickets && isFetchingNextTicketsPage && hasNextTicketsPage;

  /**
   * Effects
   */
  useEffect(() => {
    if (inView && hasNextTicketsPage) {
      fetchNextTicketsPage();
    }
  }, [fetchNextTicketsPage, hasNextTicketsPage, inView]);

  useEffect(() => {
    if (!localStorage.getItem('visibleColumns')) {
      localStorage.setItem('visibleColumns', JSON.stringify(columns));
    }
  }, [columns]);

  useEffect(() => {
    setSelectedTicketsIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRoofTop,
    isOpenStatusFilterActive,
    isCloseStatusFilterActive,
    isUnder24HoursFilterActive,
    isOver24HoursFilterActive,
    isAssignedToMeFilterActive,
    isPastMonthFilterActive,
    isPast30DaysFilterActive,
    isArchivedFilterActive,
  ]);

  const getLastMessageText = (lastMessage: LastMessageI) => {
    const messageToDisplay = lastMessage.attachmentText
      ? lastMessage.attachmentText
      : lastMessage.text;
    return messageToDisplay;
  };

  if (isSuccessfullyFetchedTickets && ticketsCount === 0) {
    return (
      <ListViewStyled>
        <ContentStyled data-testid="tickets-list">
          <TableStyled>
            <TicketsSorting selectedTicketsIds={selectedTicketsIds} columns={orderedColumns} />
          </TableStyled>
          <MessageWrapStyled>
            <Typography variant="h2" color="darkBlue" align="center">
              Tickets not found
            </Typography>
          </MessageWrapStyled>
        </ContentStyled>
      </ListViewStyled>
    );
  }
  return (
    <ListViewStyled>
      <TableStyled>
        <TicketsSorting selectedTicketsIds={selectedTicketsIds} columns={columns} />
        {isLoadingTickets && !isFetchingNextTicketsPage ? (
          <tbody>
            <tr>
              <td colSpan={visibleColumnCount}>
                <TicketsListViewLoader />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody data-testid="tickets-list">
            {ticketsPages?.map((page, index) => (
              <Fragment key={`page-${index}`}>
                {page.data?.map(
                  (
                    {
                      id,
                      roNumber,
                      lineNumber,
                      age,
                      statusId,
                      assigneeName,
                      categoryName,
                      isApprovedByDealer,
                      isViewed,
                      lastMessage,
                      hasAttachment,
                      amount,
                      claimStatus,
                      ticketPriority,
                      techNumber,
                      advisorNumber,
                      claimAge,
                      subCategory,
                      closedROAge,
                      updatedByUser,
                      updatedDateTime,
                    },
                    index
                  ) => (
                    <TrStyled
                      key={id}
                      className={`ticket-row-${index}`}
                      isViewed={isViewed}
                      isCritical={ticketPriority === 'Critical'}
                      onClick={event => handleRowClick(event, id)}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedTicketsIds.includes(id)}
                          onChange={() => {
                            handleTicketSelection(id);
                          }}
                        />
                      </td>
                      {storedVisibleColumns.map((column: Column) => (
                        <React.Fragment key={column.id}>
                          {column.label === 'Priority' && column.visible && (
                            <td>
                              <PriorityIcon priority={ticketPriority} />
                            </td>
                          )}
                          {column.label === 'RO number' && column.visible && (
                            <td>
                              <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                {roNumber}
                              </Typography>
                            </td>
                          )}
                          {column.label === 'Line number' && column.visible && (
                            <td>
                              <BreakWordsContentStyled>
                                <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                  {lineNumber || '-'}
                                </Typography>
                              </BreakWordsContentStyled>
                            </td>
                          )}
                          {column.label === 'Age (days)' && column.visible && (
                            <td style={{ textAlign: 'center' }}>
                              <Typography
                                variant={isMobile ? 'body5' : 'h5'}
                                color={mapAgeToColor(age)}
                              >
                                {age}
                              </Typography>
                            </td>
                          )}
                          {column.label === 'Claim Age (days)' && column.visible && (
                            <td style={{ textAlign: 'center' }}>
                              <Typography
                                variant={isMobile ? 'body5' : 'h5'}
                                color={claimAge ? mapAgeToColor(claimAge) : 'black'}
                              >
                                {claimAge || '-'}
                              </Typography>
                            </td>
                          )}
                          {column.label === 'Closed RO Age' && column.visible && (
                            <td style={{ textAlign: 'center' }}>
                              <Typography
                                variant={isMobile ? 'body5' : 'h5'}
                                color={closedROAge ? mapAgeToColor(closedROAge) : 'black'}
                              >
                                {closedROAge || '-'}
                              </Typography>
                            </td>
                          )}
                          {column.label === 'Status' && column.visible && (
                            <td>
                              <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                {statusId}
                              </Typography>
                            </td>
                          )}
                          {column.label === 'Assigned to:' && column.visible && (
                            <td>
                              <BreakWordsContentStyled>
                                <Typography
                                  variant={isMobile ? 'body5' : 'h5'}
                                  color={
                                    assigneeName === TicketAssigneeE.WarrCloud
                                      ? 'purple'
                                      : 'darkBlue'
                                  }
                                >
                                  {assigneeName}
                                </Typography>
                              </BreakWordsContentStyled>
                            </td>
                          )}
                          {column.label === 'Category' && column.visible && (
                            <td>
                              <BreakWordsContentStyled>
                                <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                  {categoryName}
                                </Typography>
                              </BreakWordsContentStyled>
                            </td>
                          )}
                          {column.label === 'Subcategory' && column.visible && (
                            <td>
                              <BreakWordsContentStyled>
                                <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                  {subCategory || '-'}
                                </Typography>
                              </BreakWordsContentStyled>
                            </td>
                          )}
                          {column.label === 'Last Comment' && column.visible && (
                            <td>
                              {lastMessage !== undefined && (
                                <BreakWordsContentStyled>
                                  <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                    {getLastMessageText(lastMessage)}
                                  </Typography>
                                </BreakWordsContentStyled>
                              )}
                            </td>
                          )}
                          {column.label === 'Claim Status' && column.visible && (
                            <td>
                              <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                {claimStatus}
                              </Typography>
                            </td>
                          )}
                          {column.label === 'Amount' && column.visible && (
                            <td>
                              <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                {formatAmount(amount)}
                              </Typography>
                            </td>
                          )}
                          {column.label === 'Advisor number' && column.visible && (
                            <td>
                              <BreakWordsContentStyled>
                                <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                  {advisorNumber || '-'}
                                </Typography>
                              </BreakWordsContentStyled>
                            </td>
                          )}
                          {column.label === 'Tech number' && column.visible && (
                            <td>
                              <BreakWordsContentStyled>
                                <Typography variant={isMobile ? 'body5' : 'h5'} color="darkBlue">
                                  {techNumber || '-'}
                                </Typography>
                              </BreakWordsContentStyled>
                            </td>
                          )}
                          {column.label === 'Attachment' && column.visible && (
                            <td>
                              {hasAttachment && (
                                <HasAttachmentStyled>
                                  <ApproveIcon size={isMobile ? 'xl' : '3xl'} />
                                </HasAttachmentStyled>
                              )}
                            </td>
                          )}
                          {column.label === 'Approved' && column.visible && (
                            <td>
                              {isApprovedByDealer && (
                                <ApprovedStyled>
                                  <ApproveIcon size={isMobile ? 'xl' : '3xl'} />
                                </ApprovedStyled>
                              )}
                            </td>
                          )}
                          {column.label === 'Audit Trail' && column.visible && (
                            <td>
                              <Tooltip title={updatedByUser?.displayName} placement="right" arrow>
                                <Box display="inline-block">
                                  <Typography style={{ fontWeight: 'bold' }}>
                                    {updatedByUser?.displayName
                                      ? getInitials(updatedByUser?.displayName)
                                      : '-'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                              <Typography>
                                {updatedDateTime
                                  ? format(new Date(updatedDateTime), 'dd MMM yyyy h:mm a')
                                  : null}
                              </Typography>
                            </td>
                          )}
                        </React.Fragment>
                      ))}
                    </TrStyled>
                  )
                )}
              </Fragment>
            ))}
          </tbody>
        )}
      </TableStyled>
      <span
        style={{
          display: 'block',
          width: '100%',
          height: '20px',
        }}
        ref={lastTicketObserver}
      />
      {shouldLoadMore && <DotsLoader />}
    </ListViewStyled>
  );
};

export default TicketsListView;
